import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ClientShell } from '../components/ClientShell';
import { Section, NumberField, Scale, Choice, Stepper, TextField, PhotoSlot } from '../components/ReportFields';
import { loadSaved, saveState, clearSaved } from '../lib/workoutPlayer';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const opts = { withCredentials: true };
const YES_NO = [['tak', 'Tak'], ['czesciowo', 'Częściowo'], ['nie', 'Nie']];
const MEASURES = [['waist', 'Talia'], ['hips', 'Biodra'], ['chest', 'Klatka'], ['arm', 'Ramię'], ['thigh', 'Udo']];
const EMPTY = { weight: '', measurements: {}, photos: {}, energy: null, recovery: null, sleep_hours: null, training_rating: null,
  nutrition_adherence: null, protein: null, water: null, steps: '', stress: null, stress_notes: '', difficulties: '', questions: '' };

const weekRange = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  const end = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 6);
  const f = (x) => x.toLocaleDateString('pl-PL', { day: 'numeric', month: 'long' });
  return `${f(d)} – ${f(end)}`;
};
const num = (v) => { const n = parseFloat(String(v ?? '').replace(',', '.')); return Number.isFinite(n) ? n : null; };

// Card on the client's "Dziś" screen while the weekly report is open and not
// sent yet (or when the trainer has answered the latest one).
export const ReportDueCard = () => {
  const navigate = useNavigate();
  const [st, setSt] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/client/weekly-report/status`, opts).then((r) => setSt(r.data)).catch(() => {});
  }, []);
  if (!st || !st.enabled || st.state === 'closed') return null;
  const answered = st.submitted && st.report?.trainer_comment;
  if (st.submitted && !answered) return null;
  return (
    <button type="button" onClick={() => navigate(answered ? '/client-panel#raporty' : '/raport')}
      className="glass rounded-[24px] p-5 flex items-center justify-between gap-4 text-left border border-[#C9A84C]/40" data-testid="report-due">
      <span>
        <span className="label-text text-[#C9A84C] block">{answered ? 'Trener odpowiedział' : st.state === 'overdue' ? 'Raport tygodniowy · zaległy' : 'Raport tygodniowy'}</span>
        <span className="text-sm text-[var(--text-muted)] block mt-1.5">
          {answered ? 'Zobacz komentarz do swojego raportu.' : 'Podsumuj tydzień: waga, samopoczucie, nawyki — ok. 3 minuty.'}
        </span>
      </span>
      <span className="cta-primary h-10 px-4 rounded-full text-xs font-semibold flex items-center shrink-0">{answered ? 'Zobacz' : 'Wypełnij'}</span>
    </button>
  );
};

// Weekly check-in for online coaching clients (Saturday-Tuesday window).
export const WeeklyReportPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const draftKey = status ? `hs-report:${status.week_start}` : null;

  useEffect(() => {
    axios.get(`${API_URL}/api/client/weekly-report/status`, opts).then((r) => {
      setStatus(r.data);
      const draft = loadSaved(`hs-report:${r.data.week_start}`);
      if (draft) setForm({ ...EMPTY, ...draft });
      else if (r.data.report) {
        const rep = r.data.report;
        setForm({ ...EMPTY, ...rep, weight: rep.weight ?? '', steps: rep.steps ?? '',
          measurements: Object.fromEntries(Object.entries(rep.measurements || {}).map(([k, v]) => [k, v ?? ''])) });
      }
    }).catch(() => setStatus(false));
  }, []);

  useEffect(() => { if (draftKey) saveState(draftKey, form); }, [form, draftKey]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/client/weekly-reports`, {
        ...form,
        week_start: status.week_start,
        weight: num(form.weight),
        steps: num(form.steps) != null ? Math.round(num(form.steps)) : null,
        measurements: Object.fromEntries(MEASURES.map(([k]) => [k, num(form.measurements[k])])),
        stress_notes: form.stress_notes?.trim() || null,
        difficulties: form.difficulties?.trim() || null,
        questions: form.questions?.trim() || null,
      }, opts);
      clearSaved(draftKey);
      toast.success('Raport wysłany. Dzięki!');
      navigate('/client-dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Nie udało się wysłać raportu');
    } finally {
      setSaving(false);
    }
  };

  const open = status && status.state !== 'closed';
  return (
    <ClientShell>
      <main className="max-w-3xl px-4 sm:px-8 py-6 sm:py-10 flex flex-col gap-5" data-testid="weekly-report">
        {status === null && <p className="text-[var(--text-muted)]">Ładowanie…</p>}
        {status === false && <p className="text-[var(--text-muted)]">Nie udało się wczytać raportu.</p>}
        {status && !open && (
          <p className="text-[var(--text-muted)]">Raport za ten tydzień otworzy się w sobotę.</p>
        )}
        {open && (
          <>
            <header className="flex flex-col gap-2">
              <p className="label-text text-[#C9A84C]">Raport tygodniowy{status.state === 'overdue' ? ' · zaległy' : ''}</p>
              <h1 className="big-num text-3xl sm:text-5xl">{weekRange(status.week_start)}</h1>
              <p className="text-sm text-[var(--text-muted)]">
                Treningi w tym tygodniu: {status.auto.journal_workouts} w aplikacji{status.auto.trainer_sessions ? `, ${status.auto.trainer_sessions} z trenerem` : ''}.
                {status.submitted && ' Raport już wysłany — możesz go poprawić.'}
              </p>
            </header>

            <Section title="Ciało" hint="Ważenie najlepiej rano, na czczo. Obwody i zdjęcia wystarczą co 2 tygodnie — możesz zostawić puste.">
              <NumberField label="Waga" unit="kg" value={form.weight} onChange={set('weight')} />
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {MEASURES.map(([k, l]) => (
                  <NumberField key={k} label={l} unit="cm" value={form.measurements[k]}
                    onChange={(v) => setForm((f) => ({ ...f, measurements: { ...f.measurements, [k]: v } }))} />
                ))}
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[['front', 'Przód'], ['side', 'Bok'], ['back', 'Tył']].map(([k, l]) => (
                  <PhotoSlot key={k} label={l} value={form.photos[k]} onChange={(id) => setForm((f) => ({ ...f, photos: { ...f.photos, [k]: id } }))} />
                ))}
              </div>
              <p className="text-[11px] text-[var(--text-muted)]">Zdjęcia widzisz tylko Ty i Twój trener.</p>
            </Section>

            <Section title="Regeneracja">
              <Scale label="Energia w tym tygodniu" value={form.energy} onChange={set('energy')} low="brak sił" high="pełna energia" />
              <Scale label="Regeneracja po treningach" value={form.recovery} onChange={set('recovery')} low="zakwasy, zmęczenie" high="świeżość" />
              <Stepper label="Sen — średnio na noc" value={form.sleep_hours} onChange={set('sleep_hours')} unit="h" />
            </Section>

            <Section title="Trening i nawyki">
              <Scale label="Jak oceniasz tydzień treningowy?" value={form.training_rating} onChange={set('training_rating')} low="słabo" high="świetnie" />
              <div>
                <Scale label="Odżywianie — trzymanie się zaleceń (×10%)" value={form.nutrition_adherence != null ? form.nutrition_adherence / 10 : null}
                  onChange={(v) => set('nutrition_adherence')(v != null ? v * 10 : null)} low="10%" high="100%" />
              </div>
              <Choice label="Białko w każdym posiłku" value={form.protein} onChange={set('protein')} options={YES_NO} />
              <Choice label="Woda (min. 2 l dziennie)" value={form.water} onChange={set('water')} options={YES_NO} />
              <NumberField label="Kroki — średnio dziennie" value={form.steps} onChange={set('steps')} decimal={false} />
              <Scale label="Stres" value={form.stress} onChange={set('stress')} low="spokój" high="bardzo duży" />
              <TextField label="Co powodowało stres? (opcjonalnie)" value={form.stress_notes} onChange={set('stress_notes')} />
            </Section>

            <Section title="Do trenera">
              <TextField label="Trudności i dolegliwości" value={form.difficulties} onChange={set('difficulties')}
                placeholder="Co przeszkadzało, ból, urazy, brak czasu…" />
              <TextField label="Pytania do trenera" value={form.questions} onChange={set('questions')}
                placeholder="Co chcesz wyjaśnić albo zmienić w planie?" />
            </Section>

            <button type="button" onClick={submit} disabled={saving}
              className="cta-primary w-full h-14 rounded-full text-[13px] font-bold tracking-[0.2em] uppercase disabled:opacity-50 mb-24 sm:mb-6"
              data-testid="report-submit">
              {saving ? 'Wysyłanie…' : status.submitted ? 'Zapisz zmiany' : 'Wyślij raport'}
            </button>
          </>
        )}
      </main>
    </ClientShell>
  );
};

// Client panel: own weekly reports with the trainer's answers.
export const MyReports = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState(null);
  const ref = useRef(null);
  const focus = window.location.hash === '#raporty';
  useEffect(() => {
    axios.get(`${API_URL}/api/client/weekly-reports`, opts).then((r) => setReports(r.data)).catch(() => setReports([]));
  }, []);
  useEffect(() => { if (focus && reports?.length) ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [focus, reports]);
  if (!reports || reports.length === 0) return null;
  const pl = (v) => String(v).replace('.', ',');
  return (
    <section ref={ref} className="glass rounded-[28px] p-6 sm:p-8 flex flex-col gap-2.5 scroll-mt-24 sm:scroll-mt-6" data-testid="panel-reports">
      <div className="flex items-center justify-between gap-3 mb-1">
        <h2 className="text-lg sm:text-xl font-extrabold tracking-tight">Raporty tygodniowe</h2>
        <button type="button" onClick={() => navigate('/raport')} className="cta-ghost h-9 px-3.5 rounded-full text-xs font-semibold shrink-0">Bieżący</button>
      </div>
      {reports.map((r, i) => (
        <details key={r.id} open={focus && i === 0} className="glass-soft rounded-2xl px-3.5 py-3">
          <summary className="flex items-center justify-between gap-3 cursor-pointer list-none">
            <span className="text-sm font-semibold">{weekRange(r.week_start)}</span>
            <span className={`text-xs shrink-0 ${r.trainer_comment ? 'text-[#C9A84C]' : 'text-[var(--text-muted)]'}`}>
              {r.trainer_comment ? 'komentarz trenera' : r.weight != null ? `${pl(r.weight)} kg` : 'wysłany'}
            </span>
          </summary>
          <div className="mt-3 flex flex-col gap-1.5 text-xs">
            <p className="text-[var(--text-muted)]">
              {[r.weight != null && `waga ${pl(r.weight)} kg`, r.measurements?.waist != null && `talia ${pl(r.measurements.waist)} cm`,
                r.energy && `energia ${r.energy}/10`, r.sleep_hours != null && `sen ${pl(r.sleep_hours)} h`,
                r.steps != null && `${r.steps.toLocaleString('pl-PL')} kroków`].filter(Boolean).join(' · ')}
            </p>
            {r.questions && <p>Ty: „{r.questions}”</p>}
            {r.trainer_comment && <p className="text-[#C9A84C] text-sm">Trener: „{r.trainer_comment}”</p>}
          </div>
        </details>
      ))}
    </section>
  );
};
